import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { ChevronRight, Check, Info, KeyRound, Pencil, Trash2 } from 'lucide-react';
import { useTasks } from '../context/TasksContext';
import { Sheet, Fab, SearchBox, Seg, ConfirmBox, Avatar, plural, HUE, NAVY } from '../components/AdminUi';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Korisnik' },
  { value: 'manager', label: 'Upravitelj' },
  { value: 'admin', label: 'Admin' },
];
const ROLE_BADGE = { admin: ['Admin', NAVY], manager: ['Upravitelj', HUE.prog], editor: ['Editor', HUE.done] };

// Ovlast × uloga (Korisnik, Upravitelj intervencijama, Admin).
const PERMS = [
  ['Pregled javnih zadataka i zadataka svojih grupa', 1, 1, 1],
  ['Javljanje na zadatke i intervencije', 1, 1, 1],
  ['Prijedlog novog zadatka', 1, 1, 1],
  ['Prihvaćanje poziva, status rada, zahtjev za odjavu', 1, 1, 1],
  ['Pregled članova svojih grupa', 1, 1, 1],
  ['Pregled svih zadataka', 0, 1, 1],
  ['Pokretanje, vođenje i zatvaranje intervencija', 0, 1, 1],
  ['Sudionici, lokacije i slike na intervencijama', 0, 1, 1],
  ['Kreiranje i uređivanje svih zadataka', 0, 0, 1],
  ['Odobravanje prijedloga, prijava i odjava', 0, 0, 1],
  ['Dodjela ljudi na zadatke (i obavezno)', 0, 0, 1],
  ['Korisnici, grupe, kategorije i postavke', 0, 0, 1],
];

function lastLoginText(d) {
  if (!d) return null;
  const date = new Date(d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(date); day.setHours(0, 0, 0, 0);
  const diff = Math.round((today - day) / 86400000);
  if (diff <= 0) return 'prijava danas';
  if (diff === 1) return 'prijava jučer';
  if (diff < 7) return `prijava prije ${diff} d`;
  return `prijava ${date.getDate()}. ${date.getMonth() + 1}.${date.getFullYear() !== today.getFullYear() ? ` ${date.getFullYear()}.` : ''}`;
}

function RolesSheet({ onClose }) {
  const yes = <span className="y"><Check size={17} strokeWidth={2.6} /></span>;
  const no = <span className="n">–</span>;
  return (
    <Sheet tall onClose={onClose} head={<div className="adm-sh"><div className="adm-main"><h3>Uloge i ovlasti</h3></div></div>}>
      <div className="adm-card adm-perm-wrap">
        <table className="adm-perm">
          <colgroup><col /><col className="r" /><col className="r" /><col className="r" /></colgroup>
          <thead><tr><th>Ovlast</th><th>Korisnik</th><th>Upra&shy;vitelj</th><th>Admin</th></tr></thead>
          <tbody>
            {PERMS.map(p => <tr key={p[0]}><td>{p[0]}</td><td>{p[1] ? yes : no}</td><td>{p[2] ? yes : no}</td><td>{p[3] ? yes : no}</td></tr>)}
          </tbody>
        </table>
      </div>
      <div className="adm-card" style={{ padding: '12px 13px', fontSize: 15, lineHeight: 1.5, color: '#3d4552' }}>
        <b style={{ color: HUE.violet }}>Vlasnik grupe</b> uz svoju ulogu kreira i uređuje zadatke grupe, dodjeljuje ljude na njih,
        zatvara ih i odobrava odjave. <b style={{ color: '#3d4552' }}>Član grupe</b> vidi i privatne zadatke grupe.
        Obje uloge dodjeljuju se na stranici Grupe.
      </div>
    </Sheet>
  );
}

function UserSheet({ u, me, groups, stats, onClose, onChanged, onDeleted, onShowRoles }) {
  const [role, setRole] = useState(u.role);      // nacrt uloge; sprema se tek na „Spremi ulogu”
  const [saved, setSaved] = useState('');
  const [mode, setMode] = useState(null);     // 'name' | 'password' | 'delete'
  const [name, setName] = useState(u.full_name || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const isMe = u.email === me;
  const myGroups = groups.filter(g => (g.members || []).some(m => m.email === u.email));
  const login = lastLoginText(u.last_login);

  // Vraća true ako je uspjelo.
  const run = async (fn, okText) => {
    setBusy(true); setError(''); setSaved('');
    try { await fn(); setSaved(okText); setMode(null); await onChanged(); return true; }
    catch (err) { setError(err.response?.data?.error || 'Spremanje nije uspjelo.'); return false; }
    finally { setBusy(false); }
  };
  const roleDirty = role !== u.role;
  const saveRole = async () => {
    await run(() => api.put(`/users/${encodeURIComponent(u.email)}`, { role }), 'Uloga spremljena');
  };


  return (
    <Sheet tall onClose={onClose} dirty={roleDirty} busy={busy} discardText="Nova uloga nije spremljena."
      footer={roleDirty && (
        <div className="adm-dirty">
          <div className="adm-two">
            <button type="button" className="adm-btn" disabled={busy} onClick={() => setRole(u.role)}>
              Odustani
            </button>
            <button type="button" className="adm-btn primary" disabled={busy} onClick={saveRole}>Spremi ulogu</button>
          </div>
        </div>
      )}
      head={
        <div className="adm-sh">
          <Avatar name={u.full_name || u.email} size={52} />
          <div className="adm-main">
            <h3>{u.full_name || u.email}</h3>
            <p>{u.full_name ? `${u.email} · ` : ''}{!u.is_verified ? 'čeka registraciju' : login || 'nikad se nije prijavio/la'}</p>
          </div>
        </div>
      }
    >
      <div className="adm-lbl" style={{ margin: '2px 2px -4px' }}>Zadaci {stats.year}.</div>
      <div className="adm-stats">
        <div className="adm-stat"><b>{stats.total}</b><span>{plural(stats.total, 'zadatak', 'zadatka', 'zadataka')}</span></div>
        <div className="adm-stat"><b style={{ color: HUE.prog }}>{stats.running}</b><span>u tijeku</span></div>
        <div className="adm-stat"><b style={{ color: HUE.done }}>{stats.done}</b><span>završeno</span></div>
      </div>

      <div className="adm-fl">
        Uloga
        <button type="button" className="adm-link" onClick={onShowRoles}><Info size={15} /> Što koja uloga može?</button>
      </div>
      <Seg value={role} onChange={(r) => { setRole(r); setSaved(''); }} options={ROLE_OPTIONS} disabled={isMe || busy} />
      {roleDirty && <p className="adm-note" style={{ color: HUE.amber, fontWeight: 600 }}>Nova uloga: {ROLE_OPTIONS.find(o => o.value === role)?.label}. Spremi ili odustani dolje.</p>}
      {isMe && <p className="adm-note">Svoju ulogu ne možeš mijenjati.</p>}

      <div className="adm-fl">Grupe</div>
      {myGroups.length ? (
        <div className="adm-gchips">
          {myGroups.map(g => {
            const owner = g.members.find(m => m.email === u.email)?.role === 'owner';
            return <span key={g.id}>{g.name}{owner && <b> · vlasnik</b>}</span>;
          })}
        </div>
      ) : <p className="adm-note">Nije ni u jednoj grupi.</p>}

      {saved && <p className="adm-ok">✓ {saved}</p>}
      {error && <p className="adm-err">{error}</p>}

      {mode === 'delete' ? (
        <ConfirmBox title="Obriši korisnika?"
          text={`${u.full_name || u.email} više se neće moći prijaviti. Korisnik koji već ima povijest na zadacima ili intervencijama ne može se obrisati.`}
          onCancel={() => setMode(null)} busy={busy}
          onConfirm={async () => {
            setBusy(true); setError('');
            try { await api.delete(`/users/${encodeURIComponent(u.email)}`); onDeleted(); }
            catch (err) { setError(err.response?.data?.error || 'Brisanje nije uspjelo.'); setMode(null); setBusy(false); }
          }} />
      ) : (
        <div className="adm-card" style={{ marginTop: 4 }}>
          <button type="button" className="adm-act" onClick={() => { setMode(mode === 'name' ? null : 'name'); setSaved(''); setError(''); }}><Pencil size={18} /> Uredi ime</button>
          {mode === 'name' && (
            <div className="adm-inline">
              <input id="usr-name" className="adm-input" value={name} onChange={e => setName(e.target.value)} placeholder="Ime i prezime" maxLength={100} />
              <button type="button" className="adm-btn primary" disabled={busy || !name.trim()}
                onClick={() => run(() => api.put(`/users/${encodeURIComponent(u.email)}`, { full_name: name.trim() }), 'Ime spremljeno')}>Spremi ime</button>
            </div>
          )}
          <button type="button" className="adm-act" onClick={() => { setMode(mode === 'password' ? null : 'password'); setSaved(''); setError(''); }}><KeyRound size={18} /> Postavi novu lozinku</button>
          {mode === 'password' && (
            <div className="adm-inline">
              <input id="usr-pass" type="password" className="adm-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="Najmanje 8 znakova" autoComplete="new-password" />
              <p className="adm-note">Korisnik dobiva obavijest da mu je lozinka promijenjena.</p>
              <button type="button" className="adm-btn primary" disabled={busy || password.length < 8}
                onClick={async () => { if (await run(() => api.put(`/users/${encodeURIComponent(u.email)}/reset-password`, { new_password: password }), 'Lozinka postavljena')) setPassword(''); }}>Postavi lozinku</button>
            </div>
          )}
          {!isMe && <button type="button" className="adm-act red" onClick={() => setMode('delete')}><Trash2 size={18} /> Obriši korisnika</button>}
        </div>
      )}
    </Sheet>
  );
}

function GuestSheet({ g, onClose, onChanged }) {
  const [name, setName] = useState(g.full_name);
  const [confirmDel, setConfirmDel] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged(); }
    catch (err) { setError(err.response?.data?.error || 'Spremanje nije uspjelo.'); setBusy(false); }
  };
  return (
    <Sheet onClose={onClose}
      head={<div className="adm-sh"><Avatar name={g.full_name} size={52} /><div className="adm-main"><h3>{g.full_name}</h3><p>Gost · bez korisničkog računa</p></div></div>}>
      <div className="adm-fl">Ime i prezime</div>
      <input id="guest-name" className="adm-input" value={name} onChange={e => setName(e.target.value)} maxLength={50} />
      {error && <p className="adm-err">{error}</p>}
      <button type="button" className="adm-btn primary" disabled={busy || !name.trim() || name.trim() === g.full_name}
        onClick={() => act(() => api.put(`/guests/${g.id}`, { full_name: name.trim() }))}>Spremi</button>
      {confirmDel ? (
        <ConfirmBox title="Obriši gosta?" text="Gost se uklanja i sa zadataka na kojima je upisan." busy={busy}
          onCancel={() => setConfirmDel(false)} onConfirm={() => act(() => api.delete(`/guests/${g.id}`))} />
      ) : (
        <div className="adm-card"><button type="button" className="adm-act red" onClick={() => setConfirmDel(true)}><Trash2 size={18} /> Obriši gosta</button></div>
      )}
    </Sheet>
  );
}

function AddSheet({ onClose, onDone }) {
  const [kind, setKind] = useState('user');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setError('');
    if (kind === 'user' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError('Upiši ispravnu e-mail adresu.');
    if (kind === 'guest' && !name.trim()) return setError('Upiši ime i prezime gosta.');
    setBusy(true);
    try {
      if (kind === 'user') await api.post('/users', { email: email.trim().toLowerCase(), role });
      else await api.post('/guests', { full_name: name.trim() });
      onDone(kind);
    } catch (err) {
      setError(err.response?.data?.error || 'Dodavanje nije uspjelo.');
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={onClose}
      head={<div className="adm-sh"><div className="adm-main"><h3>Dodaj</h3></div></div>}
      footer={<button type="button" className="adm-btn primary" disabled={busy} onClick={add}>{kind === 'user' ? 'Dodaj korisnika' : 'Dodaj gosta'}</button>}>
      <Seg value={kind} onChange={k => { setKind(k); setError(''); }} options={[{ value: 'user', label: 'Korisnik' }, { value: 'guest', label: 'Gost' }]} />
      {kind === 'user' ? (
        <>
          <div className="adm-fl">E-mail</div>
          <input id="add-email" className="adm-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ime.prezime@gmail.com" autoComplete="off" />
          <div className="adm-fl">Uloga</div>
          <Seg value={role} onChange={setRole} options={ROLE_OPTIONS} />
          <p className="adm-note">Nakon toga se može registrirati u aplikaciji s ovom adresom. Do tada je u popisu označen kao „čeka registraciju”.</p>
        </>
      ) : (
        <>
          <div className="adm-fl">Ime i prezime</div>
          <input id="add-guest" className="adm-input" value={name} onChange={e => setName(e.target.value)} placeholder="npr. Ivan Horvat" maxLength={50} />
          <p className="adm-note">Gost nema korisnički račun; dodaje se na zadatke radi evidencije.</p>
        </>
      )}
      {error && <p className="adm-err">{error}</p>}
    </Sheet>
  );
}

function Users({ user }) {
  const isAdmin = user?.role === 'admin';
  const { tasksByYear, fetchYear } = useTasks();
  const [users, setUsers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState('all');
  const [sheet, setSheet] = useState(null);   // { type: 'user', email } | { type: 'guest', id } | { type: 'add' } | { type: 'roles', back }
  const year = new Date().getFullYear();

  const fetchUsers = async () => { const r = await api.get('/users'); setUsers(r.data); };
  const fetchGuests = async () => { const r = await api.get('/guests'); setGuests(r.data); };
  const fetchGroups = async () => { const r = await api.get('/groups'); setGroups(r.data); };
  useEffect(() => {
    fetchUsers();
    if (isAdmin) {
      fetchGuests();
      fetchGroups();
      if (!tasksByYear[year]) fetchYear(year).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const statsFor = useMemo(() => {
    const tasks = tasksByYear[year] || [];
    const now = Date.now();
    return (email) => {
      const s = { total: 0, running: 0, done: 0, year };
      for (const t of tasks) {
        if (t.is_container || !t.assignments?.some(a => a.email === email && a.status === 'accepted')) continue;
        s.total++;
        if (t.status === 'done') s.done++;
        else if (new Date(t.start_date).getTime() <= now) s.running++;
      }
      return s;
    };
  }, [tasksByYear, year]);

  const counts = {
    all: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    manager: users.filter(u => u.role === 'manager').length,
    never: users.filter(u => u.is_verified && !u.last_login).length,
    pending: users.filter(u => !u.is_verified).length,
    guests: guests.length,
  };
  const chips = [
    ['all', 'Svi'], ['admin', 'Admini'], ['manager', 'Upravitelji'],
    ...(counts.pending ? [['pending', 'Čeka registraciju']] : []),
    ...(counts.never ? [['never', 'Nikad prijavljeni']] : []),
    ['guests', 'Gosti'],
  ];

  const q = search.trim().toLowerCase();
  const match = (name, email = '') => !q || (name || '').toLowerCase().includes(q) || email.toLowerCase().includes(q);
  const listUsers = users.filter(u => match(u.full_name, u.email) && (
    chip === 'all' || (chip === 'admin' && u.role === 'admin') || (chip === 'manager' && u.role === 'manager')
    || (chip === 'never' && u.is_verified && !u.last_login) || (chip === 'pending' && !u.is_verified)));
  const listGuests = guests.filter(g => match(g.full_name));
  const showGuests = chip === 'guests';

  const currentUser = sheet?.type === 'user' ? users.find(u => u.email === sheet.email) : null;
  const currentGuest = sheet?.type === 'guest' ? guests.find(g => g.id === sheet.id) : null;

  const userRow = (u) => {
    const badge = isAdmin && ROLE_BADGE[u.role];
    const login = lastLoginText(u.last_login);
    const meta = !isAdmin ? (u.full_name ? u.email : null)
      : !u.is_verified ? <span className="adm-s amber">čeka registraciju</span>
      : login ? login : <span className="adm-s amber">nikad se nije prijavio/la</span>;
    const inner = (
      <>
        <Avatar name={u.full_name || u.email} />
        <div className="adm-main">
          <div className="adm-t soft">{u.full_name || u.email}{u.email === user.email && <span className="me"> · ti</span>}</div>
          {meta && <div className="adm-s">{meta}</div>}
        </div>
        {badge && <span className="adm-badge" style={{ background: `${badge[1]}14`, color: badge[1] }}>{badge[0]}</span>}
      </>
    );
    return isAdmin ? (
      <button key={u.email} type="button" className="adm-it" onClick={() => setSheet({ type: 'user', email: u.email })}>
        {inner}<ChevronRight size={18} className="adm-chev" />
      </button>
    ) : <div key={u.email} className="adm-it">{inner}</div>;
  };

  return (
    <div className="adm-page">
      <div className="adm-head">
        <h2>Korisnici</h2>
        <span className="n">{users.length}{isAdmin && guests.length ? ` + ${guests.length} ${plural(guests.length, 'gost', 'gosta', 'gostiju')}` : ''}</span>
      </div>
      <SearchBox value={search} onChange={setSearch} placeholder="Pretraži po imenu ili e-mailu" />
      {isAdmin && (
        <div className="adm-chips">
          {chips.map(([k, l]) => (
            <button key={k} type="button" className={chip === k ? 'on' : ''} onClick={() => setChip(k)}>{l} <i>{counts[k]}</i></button>
          ))}
        </div>
      )}

      {showGuests ? (
        listGuests.length === 0 ? <div className="adm-empty">{q ? 'Nitko ne odgovara pretrazi.' : 'Nema gostiju.'}</div> : (
          <div className="adm-card">
            {listGuests.map(g => (
              <button key={g.id} type="button" className="adm-it" onClick={() => setSheet({ type: 'guest', id: g.id })}>
                <Avatar name={g.full_name} />
                <div className="adm-main"><div className="adm-t soft">{g.full_name}</div><div className="adm-s">gost</div></div>
                <ChevronRight size={18} className="adm-chev" />
              </button>
            ))}
          </div>
        )
      ) : listUsers.length === 0 ? (
        <div className="adm-empty">{q ? 'Nitko ne odgovara pretrazi.' : 'Nema korisnika u ovoj skupini.'}</div>
      ) : (
        <div className="adm-card">{listUsers.map(userRow)}</div>
      )}

      {isAdmin && (
        <button type="button" className="adm-link" onClick={() => setSheet({ type: 'roles' })}><Info size={16} /> Što koja uloga može?</button>
      )}

      {isAdmin && <Fab onClick={() => setSheet({ type: 'add' })} title="Dodaj korisnika ili gosta" />}

      {currentUser && (
        <UserSheet key={currentUser.email} u={currentUser} me={user.email} groups={groups} stats={statsFor(currentUser.email)}
          onClose={() => setSheet(null)} onChanged={fetchUsers}
          onDeleted={() => { setSheet(null); fetchUsers(); fetchGroups(); }}
          onShowRoles={() => setSheet({ type: 'roles', back: currentUser.email })} />
      )}
      {currentGuest && (
        <GuestSheet key={currentGuest.id} g={currentGuest} onClose={() => setSheet(null)} onChanged={() => { setSheet(null); fetchGuests(); }} />
      )}
      {sheet?.type === 'add' && (
        <AddSheet onClose={() => setSheet(null)} onDone={(kind) => { setSheet(null); if (kind === 'user') fetchUsers(); else { fetchGuests(); setChip('guests'); } }} />
      )}
      {sheet?.type === 'roles' && (
        <RolesSheet onClose={() => setSheet(sheet.back ? { type: 'user', email: sheet.back } : null)} />
      )}
    </div>
  );
}

export default Users;
